'use client';

import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { getApiToken } from '@/lib/token';
import { extractPersonIdFromToken } from '@/lib/profile-helpers';
import { mapPersonToTalent } from '@/lib/mapPersonToTalent';
import { cn } from '@/lib/utils';
import TalentCard, { TalentCardProps } from '@/components/TalentCard';
import FollowButton from '@/components/profile/FollowButton';

/**
 * Suggested creators for the signed-in viewer, from GET /api/follow/me/suggestions.
 * Returns null while loading or after a failed fetch, [] when the API confirmed
 * there is nothing to suggest, and a mapped list otherwise. Callers should
 * render nothing until a non-empty list arrives (never show an empty shell).
 */
export function useSuggestedCreators(take = 8, enabled = true): TalentCardProps[] | null {
  const [items, setItems] = useState<TalentCardProps[] | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/follow/me/suggestions?take=${take}`, { method: 'GET', cache: 'no-store' });
        if (!res.ok) return;
        const j: any = await res.json().catch(() => null);
        if (!j || !Array.isArray(j.items)) return;
        // Defensive self-filter: the API should already exclude the viewer.
        let selfId: string | null = null;
        try { selfId = extractPersonIdFromToken(await getApiToken()); } catch {}
        const mapped: TalentCardProps[] = (j.items as any[])
          .map((it) => mapPersonToTalent(it))
          .filter((t) => t.id != null && t.stageName)
          .filter((t) => !selfId || String(t.id) !== selfId)
          .map((t) => ({
            id: t.id,
            stageName: t.stageName,
            avatarUrl: t.avatarUrl || undefined,
            locationName: t.locationName || undefined,
            isVerified: t.isVerified,
          }));
        if (!cancelled) setItems(mapped);
      } catch { /* stay null; callers hide the strip */ }
    })();
    return () => { cancelled = true; };
  }, [take, enabled]);

  return enabled ? items : null;
}

export default function SuggestedCreatorsStrip({ items, className }: { items: TalentCardProps[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <div className={cn('grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4', className)}>
      {items.map((t) => (
        <div key={`suggested-${t.id}`} className="space-y-2">
          <TalentCard {...t} size="sm" />
          <div className="flex justify-center">
            <FollowButton personId={t.id} showCount={false} />
          </div>
        </div>
      ))}
    </div>
  );
}
